import { useEffect, useState } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../components/common/icon/Icon';
import { ISubsidy } from '../../../interfaces/Subsidy.interfaces';
import { formatDateWithDot } from '../../../utils/formatter/formatDate.util';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';
import { ampli } from '../../../ampli';

import { vibrateWithDuration } from '../../../utils/vibration.util';
import { SlideConfirmModal } from '../../../components/common/modal/slide/SlideConfirmModal';

interface SubsidyVerticalListViewProps {
  subsidy: ISubsidy;
}
export const SubsidyVerticalListView = ({ subsidy }: SubsidyVerticalListViewProps) => {
  const getDeadlineInfo = () => {
    // 1. Check deadlineType first
    if (subsidy.deadlineType === 'MULTIPLE') {
      return { text: '공고참조', color: '#858894' };
    }
    if (subsidy.deadlineType === 'ALWAYS') {
      return { text: '상시모집', color: '#858894' };
    }
    if (subsidy.deadlineType === 'TBD') {
      return { text: '추후공지', color: '#858894' };
    }

    // 2. Fallback to existing logic (FIXED or null)
    if (!subsidy.deadline) {
      return { text: '공고참조', color: '#858894' };
    }

    const today = new Date();
    const deadlineDate = new Date(subsidy.deadline);
    const diffDays = differenceInCalendarDays(deadlineDate, today);

    const color = diffDays < 7 ? '#FF3049' : '#858894';
    const deadlineText = diffDays === 0 ? '오늘' : `${diffDays}일 남음`;

    return { text: `마감: ${deadlineText}`, color };
  };

  const deadlineInfo = getDeadlineInfo();

  const [showPreviewSelector, setShowPreviewSelector] = useState(false);

  const handleLinkOpen = (link: string) => {
    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: link });
    } else {
      window.open(link, '_blank');
    }
  };

  const handleButton1Action = () => {
    if (subsidy.button1Type === 'PREVIEW') {
      const links = subsidy.previewLinks || [];
      if (links.length === 1) {
        handleLinkOpen(links[0]);
      } else if (links.length > 1) {
        setShowPreviewSelector(true);
      }
    } else if (subsidy.link) {
      handleLinkOpen(subsidy.link);
    }
  };

  const handleCardClick = () => {
    vibrateWithDuration(20);
    ampli.pvmClickSubsidyCard({
      city: subsidy.city ?? '',
      click_type: 'CARD',
      state: subsidy.state ?? '',
      subsidy_id: subsidy.id,
      subsidy_tag_ids: subsidy.tags?.map((tag: { id: string }) => tag.id) ?? [],
      subsidy_tag_names: subsidy.tags?.map((tag: { name: string }) => tag.name) ?? [],
      subsidy_title: subsidy.title,
    });
    handleButton1Action();
  };

  const handleButton1Click = (e: React.MouseEvent) => {
    e.stopPropagation();
    vibrateWithDuration(20);
    ampli.pvmClickSubsidyCard({
      city: subsidy.city ?? '',
      click_type: 'VIEW_BUTTON',
      state: subsidy.state ?? '',
      subsidy_id: subsidy.id,
      subsidy_tag_ids: subsidy.tags?.map((tag: { id: string }) => tag.id) ?? [],
      subsidy_tag_names: subsidy.tags?.map((tag: { name: string }) => tag.name) ?? [],
      subsidy_title: subsidy.title,
    });
    handleButton1Action();
  };

  const handleButton2Click = (e: React.MouseEvent) => {
    e.stopPropagation();
    vibrateWithDuration(20);
    ampli.pvmClickSubsidyInquiryButton({
      city: subsidy.city ?? '',
      state: subsidy.state ?? '',
      subsidy_id: subsidy.id,
      subsidy_tag_ids: subsidy.tags?.map((tag: { id: string }) => tag.id) ?? [],
      subsidy_tag_names: subsidy.tags?.map((tag: { name: string }) => tag.name) ?? [],
      subsidy_title: subsidy.title,
    });

    const rawNumber = subsidy.inquiryNumber || '';
    const cleanNumber = rawNumber.replace(/[^0-9]/g, '');
    if (cleanNumber) {
      window.location.href = `tel:${cleanNumber}`;
    }
  };

  const getPreviewLabel = (index: number) => {
    if (subsidy.previews && subsidy.previews[index]) return subsidy.previews[index];
    if (subsidy.attachmentNames && subsidy.attachmentNames[index]) return subsidy.attachmentNames[index];
    return `공고보기 ${index + 1}`;
  };

  const isNew = subsidy.postedAt && differenceInCalendarDays(new Date(), new Date(subsidy.postedAt)) <= 3;

  return (
    <Container onClick={handleCardClick}>
      <InfoSection>
        <HeaderContainer>
          <HeaderLeftSection>
            {isNew && <NewBadge>{'NEW'}</NewBadge>}
            <Region>
              {subsidy.isNationwide
                ? '전국'
                : `${
                    ['전북특별자치도', '전북'].includes(subsidy.state ?? '')
                      ? '전북'
                      : ['전라남도', '전남'].includes(subsidy.state ?? '')
                        ? '전남'
                        : subsidy.state ?? ''
                  } ${subsidy.city ?? ''}`.trim()}
              {subsidy.postedAt && <PostedAt>{` | ${formatDateWithDot(new Date(subsidy.postedAt))}`}</PostedAt>}
            </Region>
          </HeaderLeftSection>
          <HeaderRightSection>
            {subsidy.category && <Category>{subsidy.category}</Category>}
            <DeadlineSection>
              <Icon name={'Clock'} width={14} height={14} fill={deadlineInfo.color} />
              <DeadlineText style={{ color: deadlineInfo.color }}>{deadlineInfo.text}</DeadlineText>
            </DeadlineSection>
          </HeaderRightSection>
        </HeaderContainer>

        <TextSection>
          <Title>{subsidy.title}</Title>
          {subsidy.benefitSummary && (
            <BenefitSummaryContainer>
              <InfoIconWrapper>
                <Icon name={'InfoCircle'} width={16} height={16} fill={'#6c6f7d'} />
              </InfoIconWrapper>
              <BenefitSummary>{`${subsidy.benefitSummary}`}</BenefitSummary>
            </BenefitSummaryContainer>
          )}
          <BottomSection>
            <ButtonGroup>
              <ActionButton onClick={handleButton1Click}>
                {subsidy.button1Type === 'PREVIEW' && (subsidy.previewLinks || []).length > 1
                  ? `공고보기 (${(subsidy.previewLinks || []).length})`
                  : '공고보기'}
              </ActionButton>
              {subsidy.button2Visible && (
                <ActionButton onClick={handleButton2Click} $isCustom={!!subsidy.button2Label}>
                  {subsidy.button2Label || '문의하기'}
                </ActionButton>
              )}
            </ButtonGroup>
          </BottomSection>
        </TextSection>
      </InfoSection>

      <SlideConfirmModal
        isShow={showPreviewSelector}
        closeButton={{
          text: '닫기',
          handleClick: () => setShowPreviewSelector(false),
        }}
        description={undefined}
        element={
          <SubsidyPreviewList>
            <SubsidyPreviewHeaderSection>
              <SubsidyPreviewTitle>{'선택하세요'}</SubsidyPreviewTitle>
              <SubsidyPreviewDescription>{'문서뷰어로 공고를 읽을 수 있어요'}</SubsidyPreviewDescription>
            </SubsidyPreviewHeaderSection>
            {(subsidy.previewLinks || []).map((link: string, index: number) => (
              <SubsidyPreviewItem
                key={link}
                onClick={() => {
                  handleLinkOpen(link);
                  setShowPreviewSelector(false);
                }}
              >
                <SubsidyPreviewLabel>{getPreviewLabel(index)}</SubsidyPreviewLabel>
                <Icon name={'ArrowRight'} width={18} height={18} fill={'#9DA2AB'} />
              </SubsidyPreviewItem>
            ))}
          </SubsidyPreviewList>
        }
      />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: stretch;

  border-radius: 16px;
  background: #f2f2f7;
  overflow: hidden;

  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05);
  cursor: pointer;
`;

const InfoSection = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: 12px;

  padding: 20px;
`;

const HeaderContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderRightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const HeaderLeftSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 4px;
`;

const NewBadge = styled.span`
  color: #ff3049;
  font-family: Pretendard;
  font-size: 11px;
  font-weight: 700;
  line-height: normal;
`;

const Department = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%;
`;

const Region = styled.span`
  color: var(--light-scheme-color-primary-blue, #196eff);
  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  display: flex;
  align-items: center;
`;

const PostedAt = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  margin-left: 2px;
`;

const SplitBar = styled.span`
  color: var(--Light-Scheme-Monochrome-grey50, #babdca);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%;
`;

const Category = styled.span`
  color: var(--light-scheme-color-primary-blue, #196eff);
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 85%;
  background: #f0f4ff;
  padding: 4px 8px;
  border-radius: 4px;
`;

const Title = styled.h2`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  align-self: stretch;

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-overflow: ellipsis;
  word-break: break-all;
  margin: 0;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`;

const BenefitSummaryContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 1.4em; /* Match text line-height (140%) */
`;

const BenefitSummary = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  flex: 1;

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-grey80, #6c6f7d);
  text-overflow: ellipsis;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const DeadlineSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BottomSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 4px;
`;

const ViewAnnouncementButton = styled.button`
  display: flex;
  height: 28px;
  padding: 0 10px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
  background: #fff;

  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const DeadlineText = styled.span`
  color: var(--light-scheme-color-primary-red, #ff3049);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
`;

const TextSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  gap: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $isCustom?: boolean }>`
  display: flex;
  height: 36px;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: ${(props) => (props.$isCustom ? 'none' : '1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3)')};
  background: ${(props) => (props.$isCustom ? '#4A61FF' : '#fff')};
  color: ${(props) => (props.$isCustom ? '#fff' : 'var(--Light-Scheme-Monochrome-offblack, #505363)')};
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
`;

const SubsidyPreviewList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 4px 24px 4px;
`;

const SubsidyPreviewHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px 16px 16px;
`;

const SubsidyPreviewTitle = styled.h3`
  color: #232429;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
`;

const SubsidyPreviewDescription = styled.p`
  color: #858894;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  margin: 0;
`;

const SubsidyPreviewItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 20px 16px;
  border-bottom: 1px solid #f2f2f7;
  cursor: pointer;

  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background-color: #f2f2f7;
    border-radius: 12px;
  }
`;

const SubsidyPreviewLabel = styled.span`
  color: #232429;
  font-family: Pretendard;
  font-size: 17px;
  font-weight: 600;
  line-height: normal;

  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 12px;
`;

const ArrowSection = styled.div`
  display: none;
`;
